import { useQuery } from "@tanstack/react-query";

import { useAuth } from "../auth/AuthContext";
import { useAuthHeaders } from "../auth/apiClient";

export type OpenQuoteBucket =
  | "standard_cadence"
  | "thinking"
  | "approved_pending_schedule";

export type CadenceHealth =
  | "on_track"
  | "needs_audit"
  | "suppressed"
  | "not_evaluated"
  | "eligibility_unknown";

export type CadenceRailMode = "live" | "shadow" | "target";
export type CadenceChannel = "sms" | "email";
export type CadenceTouchStatus =
  | "sent"
  | "delivered"
  | "queued"
  | "preview"
  | "failed"
  | "upcoming"
  | "missed"
  | "not_evaluated"
  | "suppressed"
  | "eligibility_unknown"
  | "awaiting_previous_touch";

export interface OpenQuoteCadenceTouch {
  key: string;
  label: string;
  channel: CadenceChannel;
  status: CadenceTouchStatus;
  due_at: string | null;
  due_on: string | null;
  actual_at: string | null;
  error: string | null;
  reason: string | null;
}

export interface OpenQuoteCadenceRail {
  id: "v1_live" | "v1_shadow" | "v2_target";
  label: "V1 LIVE" | "V1 SHADOW" | "V2 TARGET";
  mode: CadenceRailMode;
  touches: OpenQuoteCadenceTouch[];
}

export interface OpenQuoteCadenceItem {
  quote_id: string;
  contact_id: number;
  customer_name: string;
  ghosted?: boolean;
  total_usd: number;
  created_on: string;
  age_days: number;
  crm_status: string;
  raw_label: string | null;
  bucket: OpenQuoteBucket;
  cadence_health: CadenceHealth;
  last_outbound_at: string | null;
  last_outbound_channel: CadenceChannel | null;
  last_inbound_at: string | null;
  last_inbound_channel: CadenceChannel | null;
  disposition: {
    state: string;
    created_at: string | null;
  } | null;
  reminder: {
    due_at: string;
    reason: string | null;
    created_by: string | null;
  } | null;
  suppression: {
    active: boolean;
    reason: string | null;
  };
  rails: OpenQuoteCadenceRail[];
}

export interface ConversionMetric {
  denominator: number;
  converted: number;
  rate: number | null;
}

export interface OpenQuotesCadenceResponse {
  schema_version: "open_quotes_cadence.v1";
  tenant_id: number;
  generated_at: string;
  generated_business_date: string;
  business_time_zone: string;
  window_days: number;
  metrics: {
    cohort_quotes: number;
    open_quotes: number;
    open_value_usd: number;
    mature_7d: ConversionMetric;
    mature_14d: ConversionMetric;
  };
  pagination: {
    total: number;
    limit: number;
    offset: number;
    has_more: boolean;
  };
  cadence_policy: Array<{
    id: OpenQuoteCadenceRail["id"];
    label: OpenQuoteCadenceRail["label"];
    mode: CadenceRailMode;
    touches: Array<{
      key: string;
      label: string;
      channel: CadenceChannel;
    }>;
  }>;
  items: OpenQuoteCadenceItem[];
}

export interface OpenQuotesCadenceQuery {
  windowDays: number;
  limit: number;
  offset: number;
}

export interface OpenQuotesCadenceIdentity {
  userId: number | null;
  tenantId: number | null;
  isDemo: boolean;
}

function boundedInteger(value: number, min: number, max: number) {
  if (!Number.isFinite(value)) return min;
  return Math.max(min, Math.min(Math.trunc(value), max));
}

export function openQuotesCadenceUrl(query: OpenQuotesCadenceQuery) {
  const params = new URLSearchParams({
    window_days: String(boundedInteger(query.windowDays, 1, 90)),
    limit: String(boundedInteger(query.limit, 1, 100)),
    offset: String(boundedInteger(query.offset, 0, Number.MAX_SAFE_INTEGER)),
  });
  return `/api/cockpit/quotes/open-cadence?${params.toString()}`;
}

export function openQuotesCadenceQueryKey(
  query: OpenQuotesCadenceQuery,
  identity: OpenQuotesCadenceIdentity,
) {
  return [
    "control-tower",
    "quotes",
    "open-cadence",
    identity.tenantId,
    identity.userId,
    identity.isDemo ? "demo" : "real",
    query.windowDays,
    query.limit,
    query.offset,
  ] as const;
}

export function useOpenQuotesCadence(query: OpenQuotesCadenceQuery) {
  const { user } = useAuth();
  const headers = useAuthHeaders();
  return useQuery({
    queryKey: openQuotesCadenceQueryKey(query, {
      userId: user?.id ?? null,
      tenantId: user?.tenant_id ?? null,
      isDemo: user?.is_demo === true,
    }),
    queryFn: async () => {
      const res = await fetch(openQuotesCadenceUrl(query), { headers });
      if (!res.ok) throw new Error(`Open quote cadence failed: ${res.status}`);
      return res.json() as Promise<OpenQuotesCadenceResponse>;
    },
    staleTime: 60_000,
  });
}
